import React, { cloneElement, useState } from 'react';
import {
  Table,
  TableBody,
  TableContainer,
  TableHead,
  Paper,
  Grid,
  Box,
} from '@mui/material';

import { makeStyles } from '@mui/styles';
import {
  SearchBoxInput,
  StyledDataCell,
  StyledTableCell,
  StyledTableRow,
} from '@pages/SuperAdminPage/styles';
import CustomPagination from '@components/Pagination';
import { StyledTypography } from './styles';
import Loader from '@components/Loader';
import Impersonate from '@components/impersonate';
import InviteStatus from '@components/IntiveStatus/inviteStatus';
import { price } from '@common/number';
import { getIntegrationFullName } from '@common/string';

interface FirmAdministratorSectionProps {
  adminData: any[];
  advisorData: any[];
}

const useStyles = makeStyles({
  root: {
    borderBottom: '1px solid #e4e7ec',
  },
  tableWrapper: {
    border: '1px solid #e4e7ec',
  },
});

function FirmAdministratorSection({
  adminData,
  advisorData,
}: FirmAdministratorSectionProps): React.JSX.Element {
  const classes = useStyles();
  const [isLoading, setIsLoading] = useState<boolean>(false);

  // Headers for Firm Administrators
  const adminHeaders: any[] = [
    { key: 'Name', value: 'name', width: '40%' },
    { key: 'Emails', value: 'email', width: '60%' },
  ];

  //   // Headers for Firm Advisors
  const advisorHeaders: any[] = [
    {
      key: 'Impersonate',
      value: 'impersonate',
      component: <Impersonate />,
      doFormat: true,
      width: '8%',
    },
    { key: 'Name', value: 'name', width: '10%' },
    { key: 'Emails', value: 'email', width: '10%' },
    { key: 'Total Clients', value: 'totalClients', width: '9%' },
    { key: 'Total AUMs', value: 'totalAum', width: '10%' },
    {
      key: 'Status',
      value: 'status',
      component: <InviteStatus />,
      doFormat: true,
      width: '8%',
    },
    { key: 'Last Login', value: 'lastLogin', width: '9%' },
    { key: 'Integrations', value: 'integrationDetails', width: '37%' },
  ];

  const TableCell = (header, row) => {
    switch (header.value) {
      case 'totalAum':
        return price(row[header.value]);
      case 'integrationDetails':
        return (
          <Box component={'span'}>
            {getIntegrationFullName(row, header) || '--'}
          </Box>
        );
      case 'lastLogin':
        return row[header?.value]?.substring(0, 10) || '--';
      case 'status':
        return cloneElement(header?.component, {
          status: row[header?.value],
          id: row['id'],
        });
      case 'impersonate':
        return cloneElement(header?.component, {
          status: row['status'],
          id: row['id'],
          userId: row['userId'],
          setIsLoading: setIsLoading,
        });
      default:
        return row[header?.value];
    }
  };

  const rowsPerPage = 10;
  const [adminPage, setAdminPage] = React.useState(1);
  const [advisorPage, setAdvisorPage] = React.useState(1);
  const [adminSearchTerm, setAdminSearchTerm] = React.useState('');
  const [advisorSearchTerm, setAdvisorSearchTerm] = React.useState('');

  const filteredAdminData = adminData?.filter(
    item =>
      item?.name?.toLowerCase().includes(adminSearchTerm.toLowerCase()) ||
      item?.email?.toLowerCase().includes(adminSearchTerm.toLowerCase()),
  );
  const filteredAdvisorData = advisorData?.filter(
    item =>
      item?.name?.toLowerCase().includes(advisorSearchTerm.toLowerCase()) ||
      item?.email?.toLowerCase().includes(advisorSearchTerm.toLowerCase()),
  );

  const adminStartIndex = (adminPage - 1) * rowsPerPage;
  const adminEndIndex = Math.min(
    adminStartIndex + rowsPerPage,
    filteredAdminData.length,
  );
  const adminDataOnCurrentPage = filteredAdminData.slice(
    adminStartIndex,
    adminEndIndex,
  );

  const advisorStartIndex = (advisorPage - 1) * rowsPerPage;
  const advisorEndIndex = Math.min(
    advisorStartIndex + rowsPerPage,
    filteredAdvisorData.length,
  );
  const advisorDataOnCurrentPage = filteredAdvisorData.slice(
    advisorStartIndex,
    advisorEndIndex,
  );

  const adminTotalCount = filteredAdminData.length;

  const advisorTotalCount = filteredAdvisorData.length;

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setAdminSearchTerm(event.target.value);
  };

  const handleAdvisorSearchChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setAdvisorSearchTerm(event.target.value);
  };

  const handleAdminPageChange = (newPage: number) => {
    setAdminPage(newPage);
  };

  const handleAdvisorPageChange = (newPage: number) => {
    setAdvisorPage(newPage);
  };

  if (isLoading) return <Loader />;

  return (
    <>
      <Grid container mt={3} rowGap={1}>
        <Grid item container xs={12}>
          <Grid
            item
            container
            xs={12}
            justifyContent={'space-between'}
            mb={1.5}
          >
            <Grid
              item
              container
              xs={6}
              justifyContent={'flex-start'}
              alignItems={'center'}
            >
              <StyledTypography
                sx={{
                  paddingBottom: '0px',
                }}
              >
                Firm Administrator
              </StyledTypography>
            </Grid>
            <Grid
              item
              container
              xs={6}
              justifyContent={'flex-end'}
              alignItems={'center'}
            >
              <SearchBoxInput
                type="text"
                placeholder="Search"
                value={adminSearchTerm}
                onChange={handleSearchChange}
                sx={{
                  marginRight: '0px',
                }}
              />
            </Grid>
          </Grid>
          <Grid
            item
            container
            xs={12}
            className={classes.tableWrapper}
            rowGap={2}
          >
            <Grid item container xs={12}>
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <StyledTableRow>
                      {adminHeaders.map(header => (
                        <StyledTableCell
                          key={header.key}
                          sx={{
                            width: header.width,
                            paddingLeft: '20px',
                          }}
                        >
                          {header.key}
                        </StyledTableCell>
                      ))}
                    </StyledTableRow>
                  </TableHead>

                  <TableBody>
                    {adminDataOnCurrentPage.map((row, index) => {
                      return (
                        <StyledTableRow
                          backgroundColor="white"
                          key={`admin- ${index}`}
                          className={classes.root}
                        >
                          {adminHeaders.map(header => (
                            <StyledTableCell
                              key={header?.key}
                              color="black"
                              style={{
                                paddingLeft: '20px',
                              }}
                            >
                              {TableCell(header, row)}
                            </StyledTableCell>
                          ))}
                        </StyledTableRow>
                      );
                    })}
                  </TableBody>
                </Table>
                <Grid item xs={12} p={'0px 15px 0px 15px'}>
                  <CustomPagination
                    page={adminPage}
                    count={adminTotalCount}
                    rowsPerPage={rowsPerPage}
                    onPageChange={handleAdminPageChange}
                  />
                </Grid>
              </TableContainer>
            </Grid>
          </Grid>
        </Grid>
        <Grid item container xs={12} mt={2}>
          <Grid
            item
            container
            xs={12}
            justifyContent={'space-between'}
            mb={1.5}
          >
            <Grid
              item
              container
              xs={6}
              justifyContent={'flex-start'}
              alignItems={'center'}
            >
              <StyledTypography
                sx={{
                  paddingBottom: '0px',
                }}
              >
                Firm Advisor
              </StyledTypography>
            </Grid>
            <Grid
              item
              container
              xs={6}
              justifyContent={'flex-end'}
              alignItems={'center'}
            >
              <SearchBoxInput
                type="text"
                placeholder="Search"
                value={advisorSearchTerm}
                onChange={handleAdvisorSearchChange}
                sx={{
                  marginRight: '0px',
                }}
              />
            </Grid>
          </Grid>
          <Grid item container xs={12} className={classes.tableWrapper}>
            <Grid item container xs={12}>
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <StyledTableRow>
                      {advisorHeaders.map(header => (
                        <StyledTableCell
                          key={header.key}
                          sx={{
                            width: header.width,
                            paddingLeft: '20px',
                          }}
                        >
                          {header.key}
                        </StyledTableCell>
                      ))}
                    </StyledTableRow>
                  </TableHead>
                  <TableBody>
                    {advisorDataOnCurrentPage.map((row, index) => {
                      return (
                        <StyledTableRow
                          key={`advisor -${index}`}
                          backgroundColor="white"
                          className={classes.root}
                        >
                          {advisorHeaders.map(header => (
                            <StyledDataCell
                              key={header?.key}
                              color="black"
                              style={{
                                paddingLeft: '20px',
                              }}
                            >
                              {TableCell(header, row)}
                            </StyledDataCell>
                          ))}
                        </StyledTableRow>
                      );
                    })}
                  </TableBody>
                </Table>
                <Grid item xs={12} p={'0px 15px 0px 15px'}>
                  <CustomPagination
                    page={advisorPage}
                    count={advisorTotalCount}
                    rowsPerPage={rowsPerPage}
                    onPageChange={handleAdvisorPageChange}
                  />
                </Grid>
              </TableContainer>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </>
  );
}

export default FirmAdministratorSection;
